import { Mail, Phone } from "lucide-react";
import { LeadForm } from "../components/LeadForm";
import { Reveal } from "../components/Reveal";
import { EMAIL, PHONE, PHONE_TEL, track, usePageMeta } from "../lib/api";

const NEXT_STEPS = [
  "We review your request.",
  "We contact you the way you prefer.",
  "We listen first.",
  "If we can help, we explain your options.",
];

const Contact = () => {
  usePageMeta(
    "Contact Show Me Shield | Talk With a Real Person",
    "Start with a conversation — not an application. Call (417) 413-2882 or request a conversation with Missouri's independent insurance brokerage.",
    { path: "/contact" }
  );

  return (
    <main className="bg-paper">
      <div className="border-b border-ink/10 bg-ink grid-lines">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
          <Reveal>
            <p className="eyebrow mb-4">Contact</p>
            <h1 className="h-display text-4xl text-white sm:text-5xl">
              Start with a conversation — not an application.
            </h1>
            <p className="mt-5 max-w-2xl text-base text-zinc-400 sm:text-lg">
              Tell us what&apos;s on your mind. A real person will follow up the way you prefer.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-5">
        <Reveal className="lg:col-span-2">
          <div className="space-y-8">
            <div className="rounded-md border-2 border-ink bg-ink p-7">
              <p className="eyebrow mb-3">Rather Talk to a Human?</p>
              <p className="font-display text-2xl font-bold uppercase text-white">Good. So would we.</p>
              <p className="mt-2 text-sm text-zinc-400">
                Call Show Me Shield and tell us what&apos;s on your mind.
              </p>
              <a
                href={PHONE_TEL}
                data-testid="contact-phone-cta"
                onClick={() => track("phone_click", { location: "contact_page" })}
                className="mt-5 flex items-center gap-3 font-mono text-xl font-bold text-lime"
              >
                <Phone className="h-5 w-5" /> {PHONE}
              </a>
              <p className="mt-3 text-xs text-zinc-500">No obligation. Start with a conversation.</p>
            </div>

            <a href={`mailto:${EMAIL}`} className="flex items-center gap-3 text-base font-medium text-ink transition-colors hover:text-lime-dim">
              <Mail className="h-5 w-5 text-lime-dim" /> {EMAIL}
            </a>

            <div>
              <h2 className="font-display text-lg font-bold uppercase tracking-wide text-ink">
                What happens next
              </h2>
              <ol className="mt-4 space-y-3">
                {NEXT_STEPS.map((s, i) => (
                  <li key={s} className="flex items-center gap-3 text-sm text-primer">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-lime-dim font-mono text-xs font-bold text-lime-dim">
                      {i + 1}
                    </span>
                    {s}
                  </li>
                ))}
              </ol>
            </div>

            <p className="text-xs leading-relaxed text-primer">
              Please don&apos;t include detailed medical information in this form. If anything sensitive
              is ever needed, we&apos;ll handle it properly during the process — never over ordinary email.
            </p>
          </div>
        </Reveal>

        <Reveal delay={120} className="lg:col-span-3">
          <div className="mb-8">
            <p className="font-display text-xl font-bold uppercase tracking-wide text-ink" data-testid="contact-trust-preamble">
              No pressure. <span className="text-lime-dim">No obligation.</span> No insurance dictionary required.
            </p>
            <p className="mt-2 text-sm text-primer">
              Tell us what you&apos;re trying to protect. We&apos;ll start with a conversation.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {["Independent Brokerage", "Missouri-Focused Guidance", "Real Human Conversations"].map((t) => (
                <span key={t} className="rounded-full border border-ink/15 bg-paper px-3.5 py-1.5 text-xs font-medium text-primer">
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div className="card-light !p-7 sm:!p-10">
            <LeadForm
              source="contact-page"
              formType="contact"
              buttonLabel="Start a Conversation"
              testId="contact-lead-form"
            />
          </div>
        </Reveal>
      </div>
    </main>
  );
};

export default Contact;
